import { type RefObject, useEffect } from 'react'
import type { Project } from '../../api/types'
import { escapeInSearch, slashFocusesSearch } from '../../lib/overviewStatus'
import { SearchIcon } from './icons'

/** The page's one search and one project filter. Both narrow every section
 *  below them (cards, queue, alarms and stations), so they sit above all
 *  of them rather than inside any one. */
export function OverviewToolbar({ search, query, onQuery, projects, project, onProject }: {
  search: RefObject<HTMLInputElement>; query: string; onQuery: (q: string) => void
  /** The reader's projects; the select shows only when they have one. */
  projects: Project[]
  /** The select's value: a project id, "none", or "" while nothing is filtered. */
  project: string; onProject: (project: string | null) => void
}) {
  useSlashToSearch(search)
  return <div className="ov-toolbar" role="search" aria-label="Filter the overview">
    <label className="ov-search">
      <SearchIcon />
      <span className="sr-only">Search stations, sensors and issues</span>
      <input ref={search} type="search" placeholder="Find a station, sensor or issue" value={query}
        autoComplete="off" spellCheck={false} onChange={event => onQuery(event.target.value)}
        onKeyDown={event => {
          if (event.key !== 'Escape') return
          if (escapeInSearch(query) === 'clear') onQuery('')
          else event.currentTarget.blur()
        }} />
      {!query && <kbd className="ov-search-key" aria-hidden="true" title="Press / to search">/</kbd>}
    </label>
    {projects.length > 0 && <label className="ov-project">
      <span>Project</span>
      <select value={project} onChange={event => onProject(event.target.value || null)}>
        <option value="">All projects</option>
        {projects.map(item => <option key={item.id} value={String(item.id)}>{item.name}</option>)}
        <option value="none">No project</option>
      </select>
    </label>}
  </div>
}

/** `/` anywhere on the page puts the cursor in the search box, unless the
 *  reader is already typing somewhere (lib/overviewStatus slashFocusesSearch)
 *  or a dialog is open: a modal owns the keyboard until it closes. */
function useSlashToSearch(target: RefObject<HTMLInputElement>) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (document.querySelector('dialog[open]')) return
      if (!slashFocusesSearch(event, event.target instanceof HTMLElement ? event.target : null)) return
      event.preventDefault()
      target.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [target])
}
